/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect, useCallback } from 'react';
import { useWallet } from '../providers/WalletProvider';
import { CLUSTERS, getCluster, getAvailableNetworks } from '../lib/clusters';
import { fetchTokenPrice } from '../data/prices';
import { Cluster, Network } from '../types/networks';
import { updateConnectionEndpoint } from '../lib/solana/solana';

export const useNetwork = () => {
    const { chain, network, setNetwork } = useWallet();
    const [currentCluster, setCurrentCluster] = useState<Cluster>(getCluster(chain || 'SOL', network));
    const [nativePrice, setNativePrice] = useState<number>(0);
    const [isLoadingPrice, setIsLoadingPrice] = useState<boolean>(false);
    
    // Custom RPC State
    const [customRpc, setCustomRpc] = useState<string>('');
    const [networkLatency, setNetworkLatency] = useState<number>(0);

    // Update cluster when wallet state changes
    useEffect(() => {
        const baseCluster = getCluster(chain || 'SOL', network);
        
        let activeRpc = baseCluster.rpcUrl;
        
        if (customRpc) {
            // Override with custom RPC if set
            setCurrentCluster({ ...baseCluster, rpcUrl: customRpc, name: `${baseCluster.name} (Custom)` });
            activeRpc = customRpc;
        } else {
            setCurrentCluster(baseCluster);
        }

        // --- Update Global Solana Connection ---
        if (chain === 'SOL') {
            updateConnectionEndpoint(activeRpc, network === 'devnet');
        }

    }, [chain, network, customRpc]);

    // Fetch Native Price when cluster changes
    useEffect(() => {
        let mounted = true;
        const loadPrice = async () => {
            setIsLoadingPrice(true);
            try {
                const priceData = await fetchTokenPrice(currentCluster.nativeCurrency.symbol);
                if (mounted) {
                    setNativePrice(priceData.price);
                }
            } catch (error) {
                console.error("Failed to fetch native price", error);
            } finally {
                if (mounted) setIsLoadingPrice(false);
            }
        };

        loadPrice();
        return () => { mounted = false; };
    }, [currentCluster.chain]);

    // Latency Ping
    useEffect(() => {
        const ping = async () => {
            const start = Date.now();
            try {
                // Determine health endpoint based on chain
                const endpoint = currentCluster.rpcUrl;
                const body = currentCluster.chain === 'SOL' 
                    ? JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'getHealth' })
                    : JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'sui_getChainIdentifier', params: [] });

                await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body
                });
                setNetworkLatency(Date.now() - start);
            } catch (e) {
                setNetworkLatency(-1); // Error
            }
        };
        
        // Initial ping and interval
        ping();
        const interval = setInterval(ping, 10000);
        return () => clearInterval(interval);
    }, [currentCluster.rpcUrl]);

    const switchNetwork = (newNetwork: Network) => {
        setNetwork(newNetwork);
        // Clear custom RPC on network switch to default to standard providers
        // unless the user specifically wants to persist it across networks (rare)
        setCustomRpc('');
    };

    const updateCustomRpc = (url: string) => {
        setCustomRpc(url);
    };

    return {
        currentCluster,
        availableClusters: getAvailableNetworks(chain || 'SOL'),
        nativePrice,
        isLoadingPrice,
        switchNetwork,
        allClusters: CLUSTERS,
        customRpc,
        setCustomRpc: updateCustomRpc,
        networkLatency
    };
};